import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';

import { API_BASE_URL } from '../../config/api';

function RouteEdit({ id, onNavigate }) {
  const { token } = useAuth();
  
  // Route fields
  const [name, setName] = useState('');
  const [journeyId, setJourneyId] = useState('');
  const [routeType, setRouteType] = useState('TRADITIONAL');
  const [description, setDescription] = useState('');
  const [active, setActive] = useState(true);

  // Lists
  const [journeys, setJourneys] = useState([]);
  const [waypoints, setWaypoints] = useState([]);
  const [facilities, setFacilities] = useState([]);

  // States
  const [fetching, setFetching] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Waypoint Editor State (for add/edit waypoint form)
  const [editingWaypoint, setEditingWaypoint] = useState(null); // null means not editing/creating, otherwise represents waypoint object (or {} for new)
  const [wpSeq, setWpSeq] = useState(1);
  const [wpName, setWpName] = useState('');
  const [wpLat, setWpLat] = useState(25.0);
  const [wpLon, setWpLon] = useState(85.0);
  const [wpRadius, setWpRadius] = useState(100);
  const [wpType, setWpType] = useState('WAYPOINT');
  const [wpDesc, setWpDesc] = useState('');
  const [wpError, setWpError] = useState(null);

  // Facility Mapping state
  const [mappingWaypointId, setMappingWaypointId] = useState(null); // ID of waypoint currently managing mappings for
  const [selectedFacilityToMap, setSelectedFacilityToMap] = useState('');

  useEffect(() => {
    loadData();
  }, [id, token]);

  const loadData = async () => {
    setFetching(true);
    const headers = { 'Authorization': `Bearer ${token}` };
    try {
      // 1. Fetch dropdown lists & facilities
      const [jRes, fRes, rRes] = await Promise.all([
        fetch(`${API_BASE_URL}/journeys`, { headers }),
        fetch(`${API_BASE_URL}/admin/facilities`, { headers }),
        fetch(`${API_BASE_URL}/routes/${id}`, { headers })
      ]);

      if (jRes.ok) setJourneys(await jRes.json());
      if (fRes.ok) setFacilities(await fRes.json());
      
      if (rRes.ok) {
        const rData = await rRes.json();
        setName(rData.name);
        setJourneyId(rData.journey_id || '');
        setRouteType(rData.route_type || 'TRADITIONAL');
        setDescription(rData.description || '');
        setActive(rData.active);
        setWaypoints(rData.waypoints || []);
      } else {
        setError('Failed to fetch route details');
      }
    } catch (e) {
      console.error('Failed to load route edit data', e);
      setError('Network error. Failed to load route details.');
    } finally {
      setFetching(false);
    }
  };

  const handleRouteSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      setError('Route name is required');
      return;
    }

    setLoading(true);
    setError(null);

    const payload = {
      name,
      journey_id: journeyId || null,
      route_type: routeType,
      description: description || null,
      active
    };

    try {
      const res = await fetch(`${API_BASE_URL}/routes/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (res.ok) {
        alert('Route details updated successfully!');
      } else {
        const errData = await res.json();
        setError(errData.detail || 'Failed to update route');
      }
    } catch (e) {
      setError('Network error. Failed to update route.');
    } finally {
      setLoading(false);
    }
  };

  // --- WAYPOINT OPERATIONS ---

  const openWaypointForm = (wp = null) => {
    setWpError(null);
    if (wp) {
      // Edit mode
      setEditingWaypoint(wp);
      setWpSeq(wp.sequence_no);
      setWpName(wp.waypoint_name || '');
      setWpLat(wp.latitude);
      setWpLon(wp.longitude);
      setWpRadius(wp.radius_meters);
      setWpType(wp.waypoint_type);
      setWpDesc(wp.description || '');
    } else {
      // Create mode
      setEditingWaypoint({});
      // Auto-increment sequence number
      const nextSeq = waypoints.length > 0 ? Math.max(...waypoints.map(w => w.sequence_no)) + 1 : 1;
      setWpSeq(nextSeq);
      setWpName('');
      setWpLat(25.0);
      setWpLon(85.0);
      setWpRadius(100);
      setWpType('WAYPOINT');
      setWpDesc('');
    }
  };

  const closeWaypointForm = () => {
    setEditingWaypoint(null);
  };

  const handleWaypointSubmit = async (e) => {
    e.preventDefault();
    if (wpLat === '' || wpLon === '') {
      setWpError('Latitude and Longitude are required');
      return;
    }

    setLoading(true);
    setWpError(null);

    const isEdit = !!editingWaypoint.id;
    const url = isEdit 
      ? `${API_BASE_URL}/routes/waypoints/${editingWaypoint.id}`
      : `${API_BASE_URL}/routes/${id}/waypoints`;
    
    const method = isEdit ? 'PUT' : 'POST';

    const payload = {
      sequence_no: parseInt(wpSeq),
      latitude: parseFloat(wpLat),
      longitude: parseFloat(wpLon),
      waypoint_name: wpName || null,
      radius_meters: parseInt(wpRadius),
      description: wpDesc || null,
      waypoint_type: wpType
    };

    try {
      const res = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (res.ok) {
        closeWaypointForm();
        // Refresh waypoints
        const rRes = await fetch(`${API_BASE_URL}/routes/${id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (rRes.ok) {
          const rData = await rRes.json();
          setWaypoints(rData.waypoints || []);
        }
      } else {
        const errData = await res.json();
        setWpError(errData.detail || 'Failed to save route point');
      }
    } catch (err) {
      setWpError('Network error. Failed to save route point.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteWaypoint = async (wpId) => {
    if (!window.confirm("Are you sure you want to delete this route point?")) return;
    try {
      const res = await fetch(`${API_BASE_URL}/routes/waypoints/${wpId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        setWaypoints(waypoints.filter(w => w.id !== wpId));
      } else {
        alert("Failed to delete waypoint");
      }
    } catch (e) {
      console.error('Failed to delete waypoint', e);
    }
  };

  // --- FACILITY MAPPING OPERATIONS ---

  const handleMapFacility = async (e) => {
    e.preventDefault();
    if (!selectedFacilityToMap) return;

    setLoading(true);
    try {
      // Find the facility to map
      const fac = facilities.find(f => f.id === selectedFacilityToMap);
      if (!fac) return;

      // Prepare payload - update waypoint_id
      const payload = {
        name: fac.name,
        facility_type: fac.facility_type,
        description: fac.description,
        contact_number: fac.contact_number,
        latitude: fac.latitude,
        longitude: fac.longitude,
        journey_id: fac.journey_id,
        organization_id: fac.organization_id,
        waypoint_id: mappingWaypointId // Map to selected waypoint
      };

      const res = await fetch(`${API_BASE_URL}/facilities/${selectedFacilityToMap}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (res.ok) {
        // Refresh facilities
        const fRes = await fetch(`${API_BASE_URL}/admin/facilities`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (fRes.ok) setFacilities(await fRes.json());
        setSelectedFacilityToMap('');
      } else {
        alert("Failed to map facility");
      }
    } catch (err) {
      console.error("Failed to map facility", err);
    } finally {
      setLoading(false);
    }
  };

  const handleUnmapFacility = async (fac) => {
    if (!window.confirm(`Are you sure you want to remove ${fac.name} from this waypoint?`)) return;
    
    setLoading(true);
    try {
      const payload = {
        name: fac.name,
        facility_type: fac.facility_type,
        description: fac.description,
        contact_number: fac.contact_number,
        latitude: fac.latitude,
        longitude: fac.longitude,
        journey_id: fac.journey_id,
        organization_id: fac.organization_id,
        waypoint_id: null // Clear the waypoint mapping
      };

      const res = await fetch(`${API_BASE_URL}/facilities/${fac.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (res.ok) {
        // Refresh facilities
        const fRes = await fetch(`${API_BASE_URL}/admin/facilities`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (fRes.ok) setFacilities(await fRes.json());
      } else {
        alert("Failed to remove facility mapping");
      }
    } catch (err) {
      console.error("Failed to unmap facility", err);
    } finally {
      setLoading(false);
    }
  };

  if (fetching) {
    return <div className="card"><p>Loading route details...</p></div>;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      
      {/* Route Edit Card */}
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h3>Edit Route Details</h3>
          <button className="btn-outline" onClick={() => onNavigate('routes')}>Back to Directory</button>
        </div>

        {error && (
          <div style={{ padding: '12px', background: '#FFEBEE', color: '#C62828', borderRadius: '6px', marginBottom: '20px' }}>
            {error}
          </div>
        )}

        <form onSubmit={handleRouteSubmit}>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '20px', marginBottom: '20px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontWeight: 600 }}>Route Name *</label>
              <input 
                type="text" 
                className="input-field" 
                value={name} 
                onChange={(e) => setName(e.target.value)} 
                style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #ccc' }}
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontWeight: 600 }}>Route Type</label>
              <select 
                value={routeType} 
                onChange={(e) => setRouteType(e.target.value)}
                style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #ccc', height: '40px' }}
              >
                <option value="TRADITIONAL">Traditional</option>
                <option value="VIP">VIP Route</option>
                <option value="EMERGENCY">Emergency Corridor</option>
              </select>
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontWeight: 600 }}>Route Status</label>
              <select 
                value={active ? 'active' : 'inactive'} 
                onChange={(e) => setActive(e.target.value === 'active')}
                style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #ccc', height: '40px' }}
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontWeight: 600 }}>Associated Journey (Season)</label>
              <select 
                value={journeyId} 
                onChange={(e) => setJourneyId(e.target.value)}
                style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #ccc', height: '40px' }}
              >
                <option value="">Independent (No Journey)</option>
                {journeys.map(j => (
                  <option key={j.id} value={j.id}>{j.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontWeight: 600 }}>Description</label>
              <input 
                type="text" 
                value={description} 
                onChange={(e) => setDescription(e.target.value)}
                style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #ccc' }}
              />
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button type="submit" className="btn-primary" disabled={loading}>
              {loading ? 'Saving...' : 'Save Route Details'}
            </button>
          </div>
        </form>
      </div>

      {/* Waypoint Editor Form Panel (if open) */}
      {editingWaypoint && (
        <div className="card" style={{ border: '2px solid #FF5722', background: '#FAFAFA' }}>
          <h4>{editingWaypoint.id ? 'Edit Route Point / Waypoint' : 'Add Route Point / Waypoint'}</h4>
          {wpError && (
            <div style={{ padding: '8px', background: '#FFEBEE', color: '#C62828', borderRadius: '4px', margin: '12px 0' }}>
              {wpError}
            </div>
          )}

          <form onSubmit={handleWaypointSubmit} style={{ marginTop: '16px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr 1fr 1fr', gap: '16px', marginBottom: '16px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: 600 }}>Seq No *</label>
                <input 
                  type="number" 
                  value={wpSeq} 
                  onChange={(e) => setWpSeq(e.target.value)}
                  style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
                  required
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: 600 }}>Waypoint Name</label>
                <input 
                  type="text" 
                  value={wpName} 
                  onChange={(e) => setWpName(e.target.value)}
                  style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
                  placeholder="e.g. Sultanpur Chowk"
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: 600 }}>Type</label>
                <select 
                  value={wpType} 
                  onChange={(e) => setWpType(e.target.value)}
                  style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc', height: '36px' }}
                >
                  <option value="WAYPOINT">Waypoint</option>
                  <option value="START">Start Point</option>
                  <option value="END">End Point</option>
                  <option value="CHECKPOINT">Checkpoint</option>
                </select>
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: 600 }}>Radius (meters)</label>
                <input 
                  type="number" 
                  value={wpRadius} 
                  onChange={(e) => setWpRadius(e.target.value)}
                  style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
                />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 2fr', gap: '16px', marginBottom: '16px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: 600 }}>Latitude *</label>
                <input 
                  type="number" 
                  step="any"
                  value={wpLat} 
                  onChange={(e) => setWpLat(e.target.value)}
                  style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
                  required
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: 600 }}>Longitude *</label>
                <input 
                  type="number" 
                  step="any"
                  value={wpLon} 
                  onChange={(e) => setWpLon(e.target.value)}
                  style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
                  required
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: 600 }}>Description</label>
                <input 
                  type="text" 
                  value={wpDesc} 
                  onChange={(e) => setWpDesc(e.target.value)}
                  style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
                  placeholder="Additional information about this point"
                />
              </div>
            </div>

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button type="button" className="btn-outline" onClick={closeWaypointForm}>Cancel</button>
              <button type="submit" className="btn-primary" disabled={loading}>
                {loading ? 'Saving...' : 'Save Point'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Facility Mapping Dialog/Panel */}
      {mappingWaypointId && (
        <div className="card" style={{ border: '2px solid #4CAF50', background: '#FAFAFA' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h4>
              Waypoint Facility Mapping: {
                waypoints.find(w => w.id === mappingWaypointId)?.waypoint_name || 
                `Sequence ${waypoints.find(w => w.id === mappingWaypointId)?.sequence_no}`
              }
            </h4>
            <button className="btn-outline btn-small" onClick={() => setMappingWaypointId(null)}>Close Mapping Panel</button>
          </div>

          {/* List of mapped facilities */}
          <div style={{ marginBottom: '20px' }}>
            <h5>Currently Mapped Facilities</h5>
            <table className="admin-table" style={{ marginTop: '8px' }}>
              <thead>
                <tr>
                  <th>Facility Name</th>
                  <th>Type</th>
                  <th>Contact</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {facilities.filter(f => f.waypoint_id === mappingWaypointId).map(f => (
                  <tr key={f.id}>
                    <td>{f.name}</td>
                    <td>{f.facility_type}</td>
                    <td>{f.contact_number || 'N/A'}</td>
                    <td>
                      <button 
                        className="btn-primary btn-small" 
                        style={{ backgroundColor: '#f44336', padding: '2px 6px', fontSize: '11px' }}
                        onClick={() => handleUnmapFacility(f)}
                      >
                        Remove Mapping
                      </button>
                    </td>
                  </tr>
                ))}
                {facilities.filter(f => f.waypoint_id === mappingWaypointId).length === 0 && (
                  <tr><td colSpan="4" style={{ textAlign: 'center', color: '#777', padding: '12px' }}>No facilities currently mapped to this waypoint.</td></tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Form to map a new facility */}
          <form onSubmit={handleMapFacility} style={{ borderTop: '1px solid #ddd', paddingTop: '16px' }}>
            <h5>Map Another Facility to this Waypoint</h5>
            <div style={{ display: 'flex', gap: '12px', marginTop: '8px' }}>
              <select
                value={selectedFacilityToMap}
                onChange={(e) => setSelectedFacilityToMap(e.target.value)}
                style={{ flex: 1, padding: '10px', borderRadius: '6px', border: '1px solid #ccc', height: '40px' }}
                required
              >
                <option value="">Choose Facility...</option>
                {facilities.filter(f => f.waypoint_id !== mappingWaypointId).map(f => (
                  <option key={f.id} value={f.id}>
                    {f.name} ({f.facility_type}) {f.waypoint_id ? '[Mapped elsewhere]' : '[Unmapped]'}
                  </option>
                ))}
              </select>
              <button type="submit" className="btn-primary" style={{ backgroundColor: '#4CAF50' }} disabled={loading || !selectedFacilityToMap}>
                Map Facility
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Route Points List Card */}
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h3>Route Points / Waypoints</h3>
          <button className="btn-primary" onClick={() => openWaypointForm()}>
            + Add Route Point
          </button>
        </div>

        <table className="admin-table">
          <thead>
            <tr>
              <th>Seq No</th>
              <th>Point Name</th>
              <th>Coordinates</th>
              <th>Type</th>
              <th>Radius (m)</th>
              <th>Mapped Facilities</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {waypoints.sort((a,b) => a.sequence_no - b.sequence_no).map((wp) => {
              const mappedCount = facilities.filter(f => f.waypoint_id === wp.id).length;
              return (
                <tr key={wp.id}>
                  <td>{wp.sequence_no}</td>
                  <td>{wp.waypoint_name || 'N/A'}</td>
                  <td>{wp.latitude.toFixed(5)}, {wp.longitude.toFixed(5)}</td>
                  <td>
                    <span className={`badge-info`} style={{ textTransform: 'capitalize' }}>
                      {wp.waypoint_type.toLowerCase()}
                    </span>
                  </td>
                  <td>{wp.radius_meters}m</td>
                  <td>
                    <span 
                      style={{ 
                        fontWeight: 'bold', 
                        color: mappedCount > 0 ? '#4CAF50' : '#888',
                        cursor: 'pointer',
                        textDecoration: 'underline'
                      }}
                      onClick={() => setMappingWaypointId(wp.id)}
                    >
                      {mappedCount} Facilities
                    </span>
                  </td>
                  <td>
                    <button 
                      className="btn-outline btn-small" 
                      style={{ marginRight: '8px', padding: '4px 8px', fontSize: '12px' }}
                      onClick={() => openWaypointForm(wp)}
                    >
                      Edit
                    </button>
                    <button 
                      className="btn-outline btn-small" 
                      style={{ marginRight: '8px', padding: '4px 8px', fontSize: '12px', borderColor: '#4CAF50', color: '#4CAF50' }}
                      onClick={() => setMappingWaypointId(wp.id)}
                    >
                      Map Facility
                    </button>
                    <button 
                      className="btn-primary btn-small" 
                      style={{ backgroundColor: '#f44336', padding: '4px 8px', fontSize: '12px' }}
                      onClick={() => handleDeleteWaypoint(wp.id)}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              );
            })}
            {waypoints.length === 0 && (
              <tr><td colSpan="7" style={{ textAlign: 'center', padding: '20px' }}>No waypoints defined for this route yet. Click "+ Add Route Point" to start.</td></tr>
            )}
          </tbody>
        </table>
      </div>

    </div>
  );
}

export default RouteEdit;
